/**
 * Icon Mappings for LP Bonds
 *
 * This file maps token symbols and DEX names to their respective icon components.
 * These mappings are used throughout the LP Bonds webapp to display correct icons.
 *
 * IMPORTANT: When adding new LP Bond types to the backend (lpbonds/src/utils/constants.ts):
 * 1. If adding a new token, add its symbol to TOKEN_ICONS below
 * 2. If adding a new DEX, add it to DEX_ICONS below
 * 3. Keys must match EXACTLY (case-sensitive) with backend BOND_TYPE_CONFIG values:
 *    - TOKEN_ICONS keys must match token0.symbol and token1.symbol
 *    - DEX_ICONS keys must match dex field
 */

import {
  // Token Icons
  GMITokenLogo,
  NRGLogoIcon,
  NRGLogoDarkIcon,
  APEIcon,
  ApeUSDIcon,
  USDTIcon,
  USDCIcon,
  EthBlueIcon,
  EthRedIcon,
  ETHFilledIcon,
  KIMAIcon,
  KIMACircleIcon,
  BNBIcon,
  BNBYellowIcon,
  PancakeSwapIcon,
  LiquidityBondsIconWhite,
  // Chain-specific icons
  ApeChainBlueIcon,
  ApeChainRedIcon,
  BerachainBlueIcon,
  ShibariumBlueIcon,
  MonadRedIcon,
  MonadSymbolFilled,
  ArbitrumBlueIcon,

  // DEX Icons
  UniswapPinkIcon,
  CamelotIcon,
  HelloIcon,
  AboreanIcon,
} from 'components/ImageComponent';

/**
 * TOKEN_ICONS - Maps token symbols to icon components
 *
 * Keys must match token0.symbol and token1.symbol from backend BOND_TYPE_CONFIG
 * Example: backend has { token0: { symbol: 'APE' } } -> use TOKEN_ICONS['APE']
 */
export const TOKEN_ICONS = {
  // Ethereum ecosystem
  ETH: ETHFilledIcon,

  // Energi ecosystem
  NRG: NRGLogoIcon,
  GMI: GMITokenLogo,

  // ApeChain ecosystem
  APE: APEIcon,
  apeUSD: ApeUSDIcon,

  // HelloDex tokens
  HELLO: HelloIcon,
  USDT: USDTIcon,

  // Stablecoins
  USDC: USDCIcon,
  'USDC.e': USDCIcon,

  // Arbitrum ecosystem
  ARB: ArbitrumBlueIcon,
  KIMA: KIMAIcon,

  // Monad ecosystem
  MON: MonadSymbolFilled,

  // Binance ecosystem
  BNB: BNBIcon,
};

/**
 * TOKEN_BG_COLORS - Background colors for token icons
 *
 * Used when displaying token icons that need a colored circular background
 * for consistent sizing and visual appearance (e.g., GetGMI chain pages).
 */
export const TOKEN_BG_COLORS = {
  ETH: '#627EEA',
  NRG: '#00E676',
  HELLO: '#000000',
  USDT: '#26A17B',
  USDC: '#2775CA',
  'USDC.e': '#2775CA',
  MON: '#6E54FF',
  APE: '#0347CD',
  apeUSD: '#0347CD',
  KIMA: '#000000',
  BNB: '#F3BA2F',
};

/**
 * TOKEN_PRICE_KEYS - Maps token symbols to useLPBondsPrices price keys
 *
 * The useLPBondsPrices hook returns prices with API keys (ethereum, apecoin, gonnamakeit, etc.)
 * This mapping converts token symbols to those price keys.
 * Stablecoins (USDT, USDC, apeUSD) are not included - they always return $1.
 *
 * Usage with useLPBondsPrices:
 *   const { prices } = useLPBondsPrices();
 *   const price = prices[TOKEN_PRICE_KEYS['ETH']]; // prices.ethereum
 */
export const TOKEN_PRICE_KEYS = {
  // Native tokens
  ETH: 'ethereum',
  NRG: 'energi',
  GMI: 'gonnamakeit',
  APE: 'apecoin',
  HELLO: 'hello-labs',
  KIMA: 'kima',
  MON: 'monad',
  BNB: 'binancecoin',
  // Arbitrum ecosystem
  ARB: 'arbitrum',

  // Wrapped tokens (same price as native)
  WETH: 'ethereum',
  WNRG: 'energi',
  WAPE: 'apecoin',
  WMON: 'monad',
  WBNB: 'binancecoin',
};

export const STABLECOINS = ['USDT', 'USDC', 'USDC.e', 'apeUSD'];

/**
 * DEX_ICONS - Maps DEX names to icon components
 *
 * All keys are lowercase. Use dex.toLowerCase() when looking up.
 * Example: getDexIcon('Uniswap') -> uses DEX_ICONS['uniswap']
 */
export const DEX_ICONS = {
  uniswap: UniswapPinkIcon,
  camelot: CamelotIcon,
  pancakeswap: PancakeSwapIcon,
  hellodex: HelloIcon,
  aborean: AboreanIcon,
};

/**
 * Default icons when symbol/dex is not found in mappings
 */
export const DEFAULT_TOKEN_ICON = EthBlueIcon;
export const DEFAULT_DEX_ICON = LiquidityBondsIconWhite;

/**
 * DEXes that require larger badge size (for visual consistency)
 * All values are lowercase.
 */
export const LARGE_DEX_BADGES = ['camelot'];

/**
 * Helper function to get token icon component
 * @param {string} symbol - Token symbol (e.g., 'ETH', 'APE', 'GMI')
 * @returns {React.Component} Icon component
 */
export const getTokenIcon = (symbol) => {
  return TOKEN_ICONS[symbol] || DEFAULT_TOKEN_ICON;
};

/**
 * Helper function to get DEX icon component
 * @param {string} dex - DEX name (e.g., 'Uniswap', 'Camelot')
 * @returns {React.Component} Icon component
 */
export const getDexIcon = (dex) => {
  return DEX_ICONS[dex?.toLowerCase()] || DEFAULT_DEX_ICON;
};

/**
 * Helper function to check if DEX needs larger badge
 * @param {string} dex - DEX name
 * @returns {boolean}
 */
export const isLargeDexBadge = (dex) => {
  return LARGE_DEX_BADGES.includes(dex?.toLowerCase());
};

/**
 * Helper function to get token price from prices object
 * Handles stablecoins automatically (returns 1 for USDT, USDC, apeUSD)
 *
 * @param {string} symbol - Token symbol (e.g., 'ETH', 'APE', 'USDT')
 * @param {Object} prices - Prices object from useLPBondsPrices hook
 * @param {Object} tokenConfig - Optional token config with isStablecoin flag
 * @returns {number} Token price in USD
 *
 * @example
 * const { prices } = useLPBondsPrices();
 * const ethPrice = getTokenPrice('ETH', prices); // prices.ethereum
 * const usdtPrice = getTokenPrice('USDT', prices); // 1 (stablecoin)
 * const apePrice = getTokenPrice('APE', prices, { isStablecoin: false }); // prices.apecoin
 */
export const getTokenPrice = (symbol, prices, tokenConfig = null) => {
  // Check if stablecoin via config or known stablecoins
  if (tokenConfig?.isStablecoin || STABLECOINS.includes(symbol)) {
    return 1;
  }
  const priceKey = TOKEN_PRICE_KEYS[symbol];
  return priceKey ? prices?.[priceKey] || 0 : 0;
};

// =============================================================================
// WALLET SIDEBAR ICON CONFIGURATION
// =============================================================================
// Used by ContractsBox.jsx for wallet balance display
// Supports native/wrapped variants and chain-specific overrides

/**
 * WALLET_TOKEN_ICONS - Maps token symbols to wallet sidebar icons
 *
 * Each entry can have:
 * - default: Icon for native/unwrapped version
 * - wrapped: Icon for wrapped version (optional, falls back to default)
 */
export const WALLET_TOKEN_ICONS = {
  // Ethereum ecosystem
  ETH: { default: EthBlueIcon, wrapped: EthRedIcon },
  WETH: { default: EthRedIcon },

  // Energi ecosystem
  NRG: { default: NRGLogoDarkIcon },
  WNRG: { default: NRGLogoDarkIcon },

  // ApeChain ecosystem
  APE: { default: ApeChainBlueIcon, wrapped: ApeChainRedIcon },
  WAPE: { default: ApeChainRedIcon },

  // Monad ecosystem
  MON: { default: MonadSymbolFilled, wrapped: MonadRedIcon },
  WMON: { default: MonadRedIcon },

  // Binance ecosystem
  BNB: { default: BNBYellowIcon, wrapped: BNBYellowIcon },
  WBNB: { default: BNBYellowIcon },

  // Berachain ecosystem
  BERA: { default: BerachainBlueIcon },

  // Shibarium ecosystem
  BONE: { default: ShibariumBlueIcon },

  // GMI (same icon across all chains)
  GMI: { default: GMITokenLogo },

  // Stablecoins
  USDT: { default: USDTIcon },
  USDC: { default: USDCIcon },
  'USDC.e': { default: USDCIcon },

  // Arbitrum ecosystem
  ARB: { default: ArbitrumBlueIcon },

  // Other tokens
  KIMA: { default: KIMACircleIcon },
  HELLO: { default: HelloIcon },
};

/**
 * Get wallet sidebar icon for a token
 * @param {string} symbol - Token symbol (e.g., 'ETH', 'WETH', 'APE')
 * @param {number} chainId - Current chain ID (unused, kept for API compatibility)
 * @param {boolean} isWrapped - Whether the token is wrapped
 * @returns {React.Component} Icon component
 */
export const getWalletTokenIcon = (symbol, _chainId, isWrapped = false) => {
  // Get icon config for symbol
  const iconConfig = WALLET_TOKEN_ICONS[symbol];
  if (!iconConfig) {
    return EthBlueIcon; // Default fallback
  }

  // Return wrapped icon if available and token is wrapped
  if (isWrapped && iconConfig.wrapped) {
    return iconConfig.wrapped;
  }

  return iconConfig.default;
};

/**
 * TOKEN_DROPDOWN_CONFIG - Special dropdown configurations for tokens
 *
 * Tokens with custom "Buy" links or special actions.
 * Tokens not listed here will get auto-generated dropdown items.
 */
export const TOKEN_DROPDOWN_CONFIG = {
  NRG: {
    buyLink: 'https://energi.world/get-nrg/',
  },
};

/**
 * Generate dropdown items for a token based on its properties
 * @param {string} symbol - Token symbol
 * @param {boolean} isNative - Whether it's a native token (ETH, NRG, APE, etc.)
 * @param {boolean} isWrapped - Whether it's a wrapped token (WETH, WNRG, etc.)
 * @returns {Array} Dropdown menu items
 */
export const getTokenDropdownItems = (symbol, isNative, isWrapped) => {
  const items = [];
  const config = TOKEN_DROPDOWN_CONFIG[symbol] || {};

  // First item: Add to wallet (for ERC20 tokens)
  items.push({
    text: `Add ${symbol}`,
    path: '/#',
    key: `${symbol}_add`,
  });

  // Second item depends on token type
  if (isNative) {
    // Native tokens can be wrapped
    items.push({
      text: `Wrap ${symbol}`,
      path: '/#',
      key: `${symbol}_wrap`,
    });
  } else if (isWrapped) {
    // Wrapped tokens can be unwrapped
    const baseSymbol = symbol.replace(/^W/, '');
    items.push({
      text: `Unwrap ${baseSymbol}`,
      path: '/#',
      key: `${symbol}_unwrap`,
    });
  } else {
    // ERC20 tokens can be bought
    items.push({
      text: `Buy ${symbol}`,
      path: config.buyLink || '/#',
      key: `${symbol}_buy`,
    });
  }

  return items;
};
